import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Award, Gift, TrendingUp, History } from 'lucide-react';
import TopBar from '../components/common/TopBar';
import BottomNav from '../components/common/BottomNav';
import LoadingSpinner from '../components/common/LoadingSpinner';
import api from '../services/api';
import { toast } from 'sonner';

const Loyalty = () => {
  const { t } = useTranslation();
  const [balance, setBalance] = useState(null);
  const [rewards, setRewards] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const [b, r, h] = await Promise.all([
        api.get('/loyalty/balance'),
        api.get('/loyalty/rewards'),
        api.get('/loyalty/transactions'),
      ]);
      setBalance(b.data);
      setRewards(r.data);
      setHistory(h.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const redeem = async (rewardId) => {
    try {
      const { data } = await api.post(`/loyalty/redeem/${rewardId}`);
      toast.success(`Resgatado! Código: ${data.voucher_code}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao resgatar');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <TopBar title={t('loyalty.title')} />
        <LoadingSpinner size="lg" />
        <BottomNav />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-20" data-testid="loyalty-page">
      <TopBar title={t('loyalty.title')} showBack />

      <div className="max-w-md mx-auto p-4 space-y-4">
        {/* Points */}
        <div className="bg-gradient-to-br from-accent to-secondary rounded-2xl p-6 text-white shadow-lg">
          <div className="flex items-center gap-2 mb-2">
            <Award className="w-5 h-5" />
            <span className="text-sm text-white/80">{t('loyalty.yourPoints')}</span>
          </div>
          <p className="font-['Outfit'] text-4xl font-bold">{balance?.balance || 0}</p>
          <p className="text-xs text-white/80 mt-2">{t('loyalty.rule')}</p>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="bg-white rounded-xl border border-border p-3">
            <TrendingUp className="w-5 h-5 text-secondary mb-1" />
            <p className="text-xs text-muted-foreground">{t('loyalty.earned')}</p>
            <p className="font-bold text-lg">{balance?.total_earned || 0}</p>
          </div>
          <div className="bg-white rounded-xl border border-border p-3">
            <Gift className="w-5 h-5 text-accent mb-1" />
            <p className="text-xs text-muted-foreground">{t('loyalty.spent')}</p>
            <p className="font-bold text-lg">{balance?.total_spent || 0}</p>
          </div>
        </div>

        {/* Rewards */}
        <div>
          <h2 className="font-['Outfit'] font-bold text-lg mb-3 flex items-center gap-2">
            <Gift className="w-5 h-5 text-primary" />
            {t('loyalty.rewards')}
          </h2>
          {rewards.length === 0 ? (
            <div className="bg-white rounded-xl border border-border p-8 text-center">
              <Gift className="w-12 h-12 mx-auto text-muted-foreground mb-2" strokeWidth={1.5} />
              <p className="text-muted-foreground">Em breve novas recompensas!</p>
            </div>
          ) : (
            <div className="space-y-3">
              {rewards.map((r) => {
                const canRedeem = (balance?.balance || 0) >= r.points_cost;
                return (
                  <div
                    key={r.id}
                    data-testid={`reward-${r.id}`}
                    className="bg-white rounded-xl border border-border p-4 flex items-center gap-3"
                  >
                    {r.image_url ? (
                      <img src={r.image_url} alt={r.name} className="w-16 h-16 object-cover rounded-lg" />
                    ) : (
                      <div className="w-16 h-16 bg-gradient-to-br from-primary/20 to-accent/20 rounded-lg flex items-center justify-center text-2xl">
                        🎁
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-foreground">{r.name}</h3>
                      <p className="text-xs text-muted-foreground line-clamp-1">{r.description}</p>
                      <p className="text-sm font-bold text-primary mt-1">{r.points_cost} pts</p>
                    </div>
                    <button
                      data-testid={`redeem-${r.id}`}
                      disabled={!canRedeem}
                      onClick={() => redeem(r.id)}
                      className="px-4 py-2 bg-primary text-primary-foreground rounded-full font-semibold text-sm disabled:opacity-50 active:scale-95"
                    >
                      {t('loyalty.redeem')}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* History */}
        <div>
          <h2 className="font-['Outfit'] font-bold text-lg mb-3 flex items-center gap-2">
            <History className="w-5 h-5 text-muted-foreground" />
            {t('loyalty.history')}
          </h2>
          {history.length === 0 ? (
            <div className="bg-white rounded-xl border border-border p-4 text-center text-muted-foreground text-sm">
              Sem transações ainda
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-border divide-y divide-border">
              {history.map((t) => (
                <div key={t.id} className="p-3 flex justify-between text-sm">
                  <div>
                    <p className="font-semibold">{t.description}</p>
                    <p className="text-xs text-muted-foreground">
                      {new Date(t.created_at).toLocaleDateString('pt-BR')}
                    </p>
                  </div>
                  <span className={`font-bold ${t.transaction_type === 'earn' ? 'text-secondary' : 'text-destructive'}`}>
                    {t.transaction_type === 'earn' ? '+' : '-'}{t.amount} pts
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <BottomNav />
    </div>
  );
};

export default Loyalty;
